import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { paymentGatewayApi } from '@/services/paymentGatewayApi';
import { useQuery, useMutation as useReactQueryMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, Search, RefreshCw, CreditCard, CheckCircle2, XCircle, Plus, Edit } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import type { PaymentGateway } from '@/types/paymentGateway';

export const PaymentGatewayListPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const { hasPermission } = useAdminAuth();

  const canView = hasPermission('view', 'paymentGateway');
  const canCreate = hasPermission('create', 'paymentGateway');
  const canEdit = hasPermission('edit', 'paymentGateway');
  const canRefresh = hasPermission('update', 'paymentGateway');

  // Fetch gateway configurations
  const {
    data: gateways = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['paymentGateways', 'configs'],
    queryFn: async () => {
      return await paymentGatewayApi.listConfigs({ includeDisabled: false });
    },
    enabled: canView,
  });

  // Refresh cache mutation
  const refreshCacheMutation = useReactQueryMutation({
    mutationFn: async (gateway: PaymentGateway) => {
      await paymentGatewayApi.refreshCache(gateway);
    },
    onSuccess: () => {
      refetch();
    },
  });

  // Filter and sort gateways
  const filteredAndSortedGateways = useMemo(() => {
    let filtered = gateways;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (gw) => {
          const merchantName = (gw.merchantName || gw.displayName || '').toLowerCase();
          const gatewayType = (gw.gatewayType || gw.gatewayCode || '').toLowerCase();
          const id = (gw.id || '').toLowerCase();
          return merchantName.includes(query) || gatewayType.includes(query) || id.includes(query);
        }
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      const enabled = filterStatus === 'enabled';
      filtered = filtered.filter((gw) => gw.enabled === enabled);
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name': {
          const nameA = (a.merchantName || a.displayName || '').toLowerCase();
          const nameB = (b.merchantName || b.displayName || '').toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        }
        case 'code': {
          const codeA = (a.gatewayType || a.gatewayCode || '').toLowerCase();
          const codeB = (b.gatewayType || b.gatewayCode || '').toLowerCase();
          comparison = codeA.localeCompare(codeB);
          break;
        }
        case 'status':
          comparison = a.enabled === b.enabled ? 0 : a.enabled ? 1 : -1;
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [gateways, searchQuery, filterStatus, sortBy, sortDirection]);

  const handleSearch = () => {
    // Search is handled by useMemo, just trigger re-render
    refetch();
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const handleRefreshCache = async (gateway: PaymentGateway) => {
    if (!window.confirm(`Refresh cache for ${gateway}?`)) return;
    await refreshCacheMutation.mutateAsync(gateway);
  };

  const getStatusColor = (enabled: boolean) => {
    return enabled
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payment Gateways</h1>
          <p className="text-muted-foreground mt-2">Manage payment gateway configurations and monitor status</p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <Button asChild>
              <Link to="/admin/payment-gateways/new">
                <Plus className="mr-2 h-4 w-4" />
                Create Gateway
              </Link>
            </Button>
          )}
          <Button variant="outline" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search gateways..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="enabled">Enabled</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Gateways Table */}
      <Card>
        <CardHeader>
          <CardTitle>Gateways ({filteredAndSortedGateways.length})</CardTitle>
          <CardDescription>List of all payment gateways and their status</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading payment gateways..." />
          ) : error || filteredAndSortedGateways.length === 0 ? (
            <EmptyState
              title="No gateways found"
              description={
                error
                  ? 'Unable to load payment gateways. Please try again.'
                  : searchQuery.trim()
                    ? 'No gateways match your search criteria.'
                    : 'No payment gateways available.'
              }
              icon={CreditCard}
              actions={[
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('name')}
                    >
                      Gateway Name
                      {sortBy === 'name' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('code')}
                    >
                      Code
                      {sortBy === 'code' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('status')}
                    >
                      Status
                      {sortBy === 'status' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedGateways.map((gateway) => {
                    const gatewayType = gateway.gatewayType || gateway.gatewayCode;
                    const merchantName = gateway.merchantName || gateway.displayName || gatewayType || 'N/A';
                    return (
                      <TableRow key={gateway.id}>
                        <TableCell className="font-medium">{merchantName}</TableCell>
                        <TableCell className="font-mono text-sm">{gatewayType}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {gateway.enabled ? (
                              <>
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(true)}`}>
                                  Enabled
                                </span>
                              </>
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 text-gray-400" />
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(false)}`}>
                                  Disabled
                                </span>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {gateway.id && (
                              <Button variant="ghost" size="icon" asChild>
                                <Link to={`/admin/payment-gateways/${gateway.id}`}>
                                  <Eye className="h-4 w-4" />
                                </Link>
                              </Button>
                            )}
                            {canEdit && gateway.id && (
                              <Button variant="ghost" size="icon" asChild>
                                <Link to={`/admin/payment-gateways/${gateway.id}/edit`}>
                                  <Edit className="h-4 w-4" />
                                </Link>
                              </Button>
                            )}
                            {canRefresh && gatewayType && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRefreshCache(gatewayType)}
                                disabled={refreshCacheMutation.isPending}
                                title="Refresh cache"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentGatewayListPage;
